// ============================================
// API Client for Dashboard
// ============================================

import type {
  DashboardSummary,
  SpendTrendData,
  ChannelPerformance,
  CampaignListResponse,
  Campaign,
  PipelineData,
  DateRange,
} from '../types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const API_TOKEN = import.meta.env.VITE_API_TOKEN || '';

/**
 * Base fetch wrapper with authentication
 */
async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(API_TOKEN && { Authorization: `Bearer ${API_TOKEN}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(error.error?.message || error.message || `HTTP ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error?.message || 'API request failed');
  }

  return data.data;
}

/**
 * Build query string from params
 */
function buildQuery(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

// ============================================
// Dashboard API
// ============================================

export async function getDashboardSummary(dateRange: DateRange): Promise<DashboardSummary> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<DashboardSummary>(`/api/dashboard/summary?${query}`);
}

export async function getSpendTrend(dateRange: DateRange): Promise<SpendTrendData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<SpendTrendData>(`/api/dashboard/spend-trend?${query}`);
}

export async function getChannelPerformance(dateRange: DateRange): Promise<{ channels: ChannelPerformance[] }> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<{ channels: ChannelPerformance[] }>(`/api/dashboard/channel-performance?${query}`);
}

export async function getConversionFunnel(dateRange: DateRange): Promise<{ stages: any[] }> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<{ stages: any[] }>(`/api/dashboard/conversion-funnel?${query}`);
}

// ============================================
// Business Unit API
// ============================================

export interface BusinessUnitData {
  ecom: {
    spend: number;
    messages: number;
    leads: number;
    impressions: number;
    clicks: number;
    costPerMessage: number;
    costPerLead: number;
  };
  const: {
    spend: number;
    messages: number;
    leads: number;
    impressions: number;
    clicks: number;
    costPerMessage: number;
    costPerLead: number;
  };
}

export async function getBusinessUnits(dateRange: DateRange): Promise<BusinessUnitData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<BusinessUnitData>(`/api/dashboard/business-units?${query}`);
}

export interface DailyConversion {
  date: string;
  messages: number;
  leads: number;
}

export async function getDailyConversions(dateRange: DateRange): Promise<DailyConversion[]> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<DailyConversion[]>(`/api/dashboard/daily-conversions?${query}`);
}

export async function getTopCampaigns(
  dateRange: DateRange,
  options: { limit?: number; sortBy?: string } = {}
): Promise<{ campaigns: Campaign[] }> {
  const query = buildQuery({
    start: dateRange.start,
    end: dateRange.end,
    limit: options.limit || 5,
    sortBy: options.sortBy || 'roas',
  });
  return fetchAPI<{ campaigns: Campaign[] }>(`/api/dashboard/top-campaigns?${query}`);
}

// ============================================
// Campaigns API
// ============================================

export async function getCampaigns(
  dateRange: DateRange,
  options: {
    platform?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
  } = {}
): Promise<CampaignListResponse> {
  const query = buildQuery({
    start: dateRange.start,
    end: dateRange.end,
    platform: options.platform,
    status: options.status,
    sortBy: options.sortBy,
    sortOrder: options.sortOrder,
    page: options.page,
    limit: options.limit,
  });
  return fetchAPI<CampaignListResponse>(`/api/campaigns?${query}`);
}

export async function getCampaignDetail(
  campaignKey: number,
  dateRange: DateRange
): Promise<{
  campaign: any;
  metrics: any;
  dailyMetrics: any[];
}> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI(`/api/campaigns/${campaignKey}?${query}`);
}

// ============================================
// Analytics API
// ============================================

export async function getTrafficData(dateRange: DateRange): Promise<{
  summary: any;
  byChannel: ChannelPerformance[];
  trend: any[];
}> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI(`/api/analytics/traffic?${query}`);
}

export async function getConversions(dateRange: DateRange): Promise<{
  summary: any;
  byChannel: any[];
  trend: any[];
}> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI(`/api/analytics/conversions?${query}`);
}

// ============================================
// Pipeline API
// ============================================

export async function getPipelineData(dateRange: DateRange): Promise<PipelineData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<PipelineData>(`/api/leads/pipeline?${query}`);
}

export async function getLeadsBySource(dateRange: DateRange): Promise<{ sources: any[] }> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<{ sources: any[] }>(`/api/leads/by-source?${query}`);
}

export async function getLeadStages(): Promise<{ stages: any[] }> {
  return fetchAPI<{ stages: any[] }>('/api/leads/stages');
}

// ============================================
// ETL Status API
// ============================================

export async function getETLStatus(): Promise<{ sources: any[] }> {
  return fetchAPI<{ sources: any[] }>('/api/etl/status');
}

// ============================================
// Marketing Funnel API (Monday.com data)
// ============================================

export interface MarketingFunnelStage {
  stageName: string;
  stageOrder: number;
  count: number;
  conversionRate: number | null;
  value?: number;
  color: string;
}

export interface MarketingFunnelBySource {
  leadSource: string;
  leads: number;
  mqls: number;
  consultations: number;
  wons: number;
  closed: number;
  gmv: number;
  leadToMqlRate: number;
  leadToWonRate: number;
}

export interface MarketingFunnelTrend {
  date: string;
  leads: number;
  mqls: number;
  consultations: number;
  wons: number;
  closed: number;
  gmv: number;
}

export interface MarketingFunnelData {
  summary: {
    totalLeads: number;
    totalMqls: number;
    totalConsultations: number;
    totalWons: number;
    totalClosed: number;
    totalGmv: number;
    leadToMqlRate: number;
    mqlToConsultationRate: number;
    consultationToWonRate: number;
    leadToWonRate: number;
  };
  stages: MarketingFunnelStage[];
  bySource: MarketingFunnelBySource[];
  trend: MarketingFunnelTrend[];
}

export async function getMarketingFunnel(dateRange: DateRange): Promise<MarketingFunnelData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<MarketingFunnelData>(`/api/leads/marketing-funnel?${query}`);
}

// ============================================
// CONST Marketing Funnel API
// Facebook Ads → CS Board → MQL → Consultation → Won
// ============================================

export interface ConstFunnelKPIs {
  totalSpend: number;
  totalImpressions: number;
  totalReach: number;
  totalClicks: number;
  ctr: number;
  cpm: number;
  cpc: number;
  leadsFromAds: number;
  actualLeads: number;
  costPerLead: number;
  mqls: number;
  consultations: number;
  wons: number;
  gmv: number;
  roas: number;
}

export interface ConstFunnelStage {
  stage: string;
  label: string;
  value: number;
  conversionRate: number | null;
  costPerUnit: number;
  gmv?: number;
}

export interface ConstAdPerformance {
  adId: string;
  adName: string;
  campaignName: string;
  spend: number;
  impressions: number;
  clicks: number;
  messagesReported: number;
  leadsReported: number;
  actualLeads: number;
  mqls: number;
  consultations: number;
  wons: number;
  gmv: number;
  costPerActualLead: number;
  leadMatchRate: number;
}

export interface ConstFunnelData {
  kpis: ConstFunnelKPIs;
  funnel: ConstFunnelStage[];
  adPerformance: ConstAdPerformance[];
}

export async function getConstFunnel(dateRange: DateRange): Promise<ConstFunnelData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<ConstFunnelData>(`/api/dashboard/const-funnel?${query}`);
}

export interface ConstDailyTrend {
  date: string;
  dayName: string;
  spend: number;
  impressions: number;
  clicks: number;
  leadsReported: number;
  actualLeads: number;
  mqls: number;
  consultations: number;
  wons: number;
}

export async function getConstDailyTrend(dateRange: DateRange): Promise<ConstDailyTrend[]> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<ConstDailyTrend[]>(`/api/dashboard/const-daily-trend?${query}`);
}

// ============================================
// ECOM Marketing Funnel API
// Facebook Ads → Order Board → QL → Quotation → Won → Closed
// ============================================

export interface EcomFunnelKPIs {
  totalSpend: number;
  totalImpressions: number;
  totalReach: number;
  totalClicks: number;
  ctr: number;
  cpm: number;
  cpc: number;
  messagesFromAds: number;
  leadsFromAds: number;
  actualLeads: number;
  leadsFromAdsMonday: number;  // Leads with ad_id in Monday
  leadsOrganic: number;        // Leads without ad_id in Monday
  costPerLead: number;
  qls: number;
  quotations: number;
  wons: number;
  closed: number;
  gmv: number;
  roas: number;
}

export interface EcomFunnelStage {
  stage: string;
  label: string;
  value: number;
  conversionRate: number | null;
  costPerUnit: number;
  gmv?: number;
}

export interface EcomAdPerformance {
  adId: string;
  adName: string;
  campaignName: string;
  spend: number;
  impressions: number;
  clicks: number;
  messagesReported: number;
  leadsReported: number;
  actualLeads: number;
  qls: number;
  quotations: number;
  wons: number;
  closed: number;
  gmv: number;
  costPerActualLead: number;
  leadMatchRate: number;
}

export interface EcomFunnelData {
  kpis: EcomFunnelKPIs;
  funnel: EcomFunnelStage[];
  adPerformance: EcomAdPerformance[];
}

export async function getEcomFunnel(dateRange: DateRange): Promise<EcomFunnelData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<EcomFunnelData>(`/api/dashboard/ecom-funnel?${query}`);
}

export interface EcomDailyTrend {
  date: string;
  dayName: string;
  spend: number;
  impressions: number;
  clicks: number;
  messagesReported: number;
  fbLeads: number;
  actualLeads: number;
  qls: number;
  quotations: number;
  wons: number;
  closed: number;
}

export async function getEcomDailyTrend(dateRange: DateRange): Promise<EcomDailyTrend[]> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<EcomDailyTrend[]>(`/api/dashboard/ecom-daily-trend?${query}`);
}

// ============================================
// ECOM LEAD Campaigns API
// Performance data for LEAD objective campaigns only
// ============================================

export interface EcomLeadCampaignsSummary {
  totalSpend: number;
  totalLeads: number;
  costPerLead: number;
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
}

export interface EcomLeadAdSet {
  adSetId: string;
  adSetName: string;
  campaignName: string;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  costPerLead: number;
}

export interface EcomLeadCampaignsData {
  summary: EcomLeadCampaignsSummary;
  adSets: EcomLeadAdSet[];
}

export async function getEcomLeadCampaigns(dateRange: DateRange): Promise<EcomLeadCampaignsData> {
  const query = buildQuery({ start: dateRange.start, end: dateRange.end });
  return fetchAPI<EcomLeadCampaignsData>(`/api/dashboard/ecom-lead-campaigns?${query}`);
}
